import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { CommonModule } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PrimaryVirtualLocationHttpService } from '@sondermind/data-access/provider-facing/provider-profile';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { IrisButtonContainerAlignments, IrisButtonModule } from '@sondermind/iris/button';
import { IrisFormFieldModule } from '@sondermind/iris/form-field';
import { IrisTextInputModule } from '@sondermind/iris/text-input';
import { IrisCardModule } from '@sondermind/iris/card';
import { GoogleMapsAutoCompleteDirective } from '@sondermind/google-maps';

import { Address, AddressFormGroup } from '@sondermind/utilities/models-location';
import { ZipcodeValidator } from '@sondermind/utilities/tools';
import { StateCodeValidator } from '@sondermind/utilities/tools/validators/state-code.validator';

import { setAddressObject, setServerValidationErrors } from '@sondermind/core/utilities/autocomplete-address-selector';

@Component({
  selector: 'sondermind-primary-virtual-address-card',
  templateUrl: './primary-virtual-address-card.component.html',
  styleUrls: ['./primary-virtual-address-card.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinner,
    IrisButtonModule,
    IrisFormFieldModule,
    IrisTextInputModule,
    IrisCardModule,
    GoogleMapsAutoCompleteDirective,
  ]
})
export class PrimaryVirtualAddressCardComponent implements OnInit {
  @Input() set primaryVirtualAddress(addr: Address) {
    this.initForm(addr);
  }

  pvlForm: AddressFormGroup;
  saving = false;
  saveFailure = false;
  serverValidationFailedCount = 0;
  saveAfterUpdate = false;
  pvlErrors = {
    zipcodeFormat: 'Zipcode must be numeric and no more than 9 digits',
    invalidAddress: 'Can\'t verify address',
    stateCode: 'Must be valid uppercase state abbreviation',
    streetNumber: 'Street Address is unconfirmed',
    subpremise: 'Apartment, unit, suite, or floor is missing or unconfirmed',
    postalCode: 'Zip Code is unconfirmed',
    locality: 'City is unconfirmed',
    administrativeAreaLevel1: 'State is unconfirmed'
  };
  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  constructor(
    private appService: SondermindApplicationService,
    private snackBar: MatSnackBar,
    private pvlService: PrimaryVirtualLocationHttpService) {}

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(address?: Address): void {
    // Not a fan of snake case here.. But this is what the backend expects.
    this.pvlForm = new FormGroup({
      address_line1: new FormControl(address?.addressLine1 || '', Validators.required),
      address_line2: new FormControl(address?.addressLine2 || ''),
      city: new FormControl(address?.city || '', Validators.required),
      state: new FormControl(address?.state || '', [Validators.required, StateCodeValidator.format]),
      postal_code: new FormControl(address?.postalCode || '', [Validators.required, ZipcodeValidator.format]),
    }) as AddressFormGroup;
  }

  enterSubmit(event): void {
    event.preventDefault();
  }

  locationSelected(e): void {
    setAddressObject(e.address_components,
      this.pvlForm,
      {
        city: 'city',
        state: 'state',
        addressLine1: 'address_line1',
        addressLine2: 'address_line2',
        postalCode: 'postal_code'
      });
  }

  savePvl(): void {
    if (!this.pvlForm.valid) {
      return;
    }
    this.saveFailure = false;
    this.saving = true;
    this.pvlService.updatePrimaryVirtualAddress(this.appService.currentUser.personaProviderId,
      {
        primary_virtual_location: this.pvlForm.getRawValue()
      }
    )
      .subscribe({
        next: () => {
          this.saving = false;
          this.snackBar.open('Address Saved', 'Dismiss', {
            duration: 2000,
            panelClass: 'snack-bar-success'
          });
        },
        error: (err) => {
          setServerValidationErrors(
            this.pvlForm,
            err,
            (value: boolean) => {
              this.saveFailure = value;
            },
            () => {
              this.serverValidationFailedCount += 1;
            }
          );
          this.appService.publishErrors(err);
          this.saving = false;
          this.snackBar.open(`Address NOT Saved`, 'Dismiss', {
            duration: 2000,
            panelClass: 'snack-bar-success'
          });
        }
      });
  }
}
